/**
 * BlogCard Component
 *
 * Displays a blog post preview in a card format
 */

import { Link } from "react-router-dom";
import { Calendar, Clock, Users } from "lucide-react";
import { Card, Badge } from "@bcc/shared-ui";
import type { BlogPost } from "../../content/blog/types";
import { CATEGORY_INFO } from "../../content/blog/types";

interface BlogCardProps {
  post: BlogPost;
  variant?: "default" | "featured" | "compact";
}

export function BlogCard({ post, variant = "default" }: BlogCardProps) {
  const categoryInfo = CATEGORY_INFO[post.category];

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (variant === "compact") {
    return (
      <Link to={`/blog/${post.slug}`} className="block group">
        <Card
          variant="outlined"
          className="p-4 hover:border-teal-300 hover:shadow-md transition-all duration-200"
        >
          <div className="flex gap-4">
            {post.featuredImage && (
              <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors line-clamp-2">
                {post.title}
              </h3>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {post.readingTime} min read
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDate(post.publishedAt)}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link to={`/blog/${post.slug}`} className="block group">
        <Card
          variant="outlined"
          padding="none"
          className="overflow-hidden hover:border-teal-300 hover:shadow-lg transition-all duration-200"
        >
          {post.featuredImage && (
            <div className="h-48 md:h-64 overflow-hidden bg-gray-100">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="info">{categoryInfo.label}</Badge>
              <span className="text-xs text-gray-500">
                {post.readingTime} min read
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors mb-3">
              {post.title}
            </h2>
            <p className="text-gray-600 line-clamp-3 mb-4">
              {post.description}
            </p>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-teal-600" />
                <span>Builder Buddy Team</span>
              </div>
              <span className="text-gray-300">|</span>
              <span>{formatDate(post.publishedAt)}</span>
            </div>
          </div>
        </Card>
      </Link>
    );
  }

  // Default variant
  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <Card
        variant="outlined"
        className="p-5 hover:border-teal-300 hover:shadow-md transition-all duration-200 h-full flex flex-col"
      >
        {post.featuredImage && (
          <div className="h-40 -mx-5 -mt-5 mb-4 overflow-hidden bg-gray-100 rounded-t-lg">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          </div>
        )}
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="info" className="text-xs">
            {categoryInfo.label}
          </Badge>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-2 line-clamp-2">
          {post.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2 mb-4 flex-grow">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-teal-600" />
            <span>Builder Buddy Team</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {post.readingTime} min
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function BlogCardSkeleton({
  variant = "default",
}: {
  variant?: "default" | "featured" | "compact";
}) {
  if (variant === "compact") {
    return (
      <Card variant="outlined" className="p-4 animate-pulse">
        <div className="flex gap-4">
          <div className="w-20 h-20 bg-gray-200 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-3 bg-gray-200 rounded w-1/3 mt-3" />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="outlined" className="p-5 animate-pulse">
      <div className="h-40 bg-gray-200 rounded -mx-5 -mt-5 mb-4" />
      <div className="h-5 bg-gray-200 rounded w-1/4 mb-3" />
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-4 bg-gray-200 rounded w-full mb-1" />
      <div className="h-4 bg-gray-200 rounded w-2/3 mb-4" />
      <div className="h-3 bg-gray-200 rounded w-1/2 mt-4 pt-3 border-t border-gray-100" />
    </Card>
  );
}
